// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React from 'react'
import PropTypes from 'prop-types'
import { Box, Tag } from 'grommet'
import { useTranslation } from 'react-i18next'

import { DataTable, Typography } from '../../../../components'

export const PillItemAdder = ({
  addForm,
  itemList = [],
  heading,
  onDelete,
  testId,
  tagsAssigned = '',
  isDeviceTagsModal = false,
  deviceData = []
}) => {
  const { t } = useTranslation(['device', 'licensing', 'common'])
  const deviceColumns = [
    {
      property: 'display_device_type',
      type: 'string',
      header: t('device_type'),
      primary: true,
      render: (datum) => {
        return (
          <Box direction="row" align="center">
            <Typography type="text" wordBreak="break-all" testId="device-type">
              {datum?.display_device_type}
            </Typography>
          </Box>
        )
      }
    },
    {
      property: 'serial_number',
      type: 'string',
      header: t('serial_number'),
      primary: true,
      render: (datum) => {
        return (
          <Box direction="row" align="center">
            <Typography
              type="text"
              wordBreak="break-all"
              testId="serial-number"
            >
              {datum?.serial_number}
            </Typography>
          </Box>
        )
      }
    },
    {
      property: 'device_model',
      type: 'string',
      header: t('device_model'),
      primary: true,
      render: (datum) => {
        return (
          <Box direction="row" align="center">
            <Typography type="text" wordBreak="break-all" testId="device-model">
              {datum?.device_model}
            </Typography>
          </Box>
        )
      }
    }
  ]
  const tagsEls = itemList.map((item, idx) => {
    return (
      <Box
        margin="xsmall"
        gap="medium"
        alignSelf="start"
        data-testid={`assigned-tags-${idx}`}
        key={`pill-${idx}`} /* eslint-disable-line react/no-array-index-key */
      >
        <Tag
          testId={`${item?.name}-${item?.value}`}
          name={item?.name}
          value={item?.value}
          onRemove={() => {
            onDelete(item)
          }}
        />
      </Box>
    )
  })
  return (
    <Box data-testid={testId}>
      {isDeviceTagsModal ? (
        <>
          <Box
            margin={{ top: 'medium', bottom: 'small' }}
            border={{ side: 'bottom', color: 'border-weak' }}
          >
            <Typography level="2" type="heading" testId="selected-tags-header">
              {deviceData?.length > 1
                ? t('device:tags.manage_device_tags_dlg_plural_title')
                : t('device:tags.manage_device_tags_dlg_singular_title')}
            </Typography>
          </Box>

          <Box margin={{ bottom: 'small' }}>
            {itemList?.length ? (
              <Box
                data-testid="assigned-tags"
                direction="row"
                wrap
                style={{ overflow: 'auto' }}
              >
                {tagsEls}
              </Box>
            ) : (
              <Box data-testid="no-tags-assigned-description">
                {tagsAssigned}
              </Box>
            )}
          </Box>
          <Box height={{ max: 'small' }} margin={{ bottom: 'small' }}>
            <DataTable
              testId="device-tag-details-table"
              grid={{
                columns: deviceColumns,
                data: deviceData
              }}
            />
          </Box>
          {addForm}
        </>
      ) : (
        <>
          {addForm}

          <Box
            height={{ min: 'auto' }}
            margin={{ top: 'medium', bottom: 'small' }}
            border={{ side: 'bottom', color: 'border-weak' }}
          >
            <Typography level="2" type="heading" testId="selected-tags-header">
              {heading}
            </Typography>
          </Box>
          <Box height="small">
            {itemList && itemList.length > 0 ? (
              <Box
                data-testid="assigned-tags"
                direction="row"
                wrap
                style={{ overflow: 'auto' }}
              >
                {tagsEls}
              </Box>
            ) : (
              <Box data-testid="no-tags-assigned-description">
                {tagsAssigned}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

PillItemAdder.propTypes = {
  /**
   * Add Component of type AddForm
   */
  addForm: PropTypes.element.isRequired,

  /**
   * Pill data
   */
  itemList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string
    })
  ),

  /**
   * Heading string for section showing added items/pills
   */
  heading: PropTypes.string.isRequired,
  tagsAssigned: PropTypes.string,

  /**
   * Delete handler
   */
  onDelete: PropTypes.func.isRequired,

  /**
   * Test id
   */
  testId: PropTypes.string.isRequired,

  /**
   * isDeviceTagsModal
   */
  isDeviceTagsModal: PropTypes.bool,

  /**
   * DeviceData
   */
  deviceData: PropTypes.array
}
